import React, { useEffect, useState } from 'react';
import { Table, Stack, Divider, SelectPicker, Button, Tag } from 'rsuite';
import { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import TablePagination from '@/components/TablePagination';
import FetchingSelect from '@/components/FetchingSelect';
import ImageCell from '@/components/ImageCell';
import { BaseErrorModel, PaginationFilterResult } from '@/models/core.model';
import {
  EnergyHubReceipt, EnergyHubReceiptMatchSource, EnergyHubReceiptMatchSourceList, EnergyHubReceiptPaginationFilter,
  EnergyHubReceiptStatus, EnergyHubReceiptStatusList, energyHubTimeZone, formatHkd,
} from '@/models/energy-hub.model';
import { getEnergyHubsLookup, getPagedEnergyHubReceipts } from '@/services/energy-hub.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';
import { EnergyHubReceiptStatusTag } from '../list/energy-hub-status-tag';
import ReceiptReviewDrawer from './receipt-review-drawer';

const { Column, HeaderCell, Cell } = Table;

interface Props {
  /** When set, only this hub's receipts are listed (hub detail tab). */
  energyHubId?: number;
  /** Called after an approve / reject, which can move the hub's credit. */
  onReviewed?: () => void;
}

const EnergyHubReceiptList: React.FC<Props> = ({ energyHubId, onReviewed }) => {
  const [filterReq, setFilterReq] = useState<EnergyHubReceiptPaginationFilter>({
    ...new EnergyHubReceiptPaginationFilter(), status: EnergyHubReceiptStatus.Pending,
  });
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<EnergyHubReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState<EnergyHubReceipt | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    fetchData();
  }, [filterReq, energyHubId, reloadKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getPagedEnergyHubReceipts({ ...filterReq, energyHubId: energyHubId ?? filterReq.energyHubId });
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching receipts: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="flex-end" spacing={6}>
        {!energyHubId && (
          <FetchingSelect
            label="Hub"
            fetchData={(text, page) => getEnergyHubsLookup(text, page)}
            value={filterReq.energyHubId}
            onChange={val => setFilterReq(pre => ({ ...pre, pageNumber: 1, energyHubId: val ?? undefined }))}
            style={{ width: 260 }}
          />
        )}
        <SelectPicker
          label="Status"
          data={EnergyHubReceiptStatusList}
          searchable={false}
          value={filterReq.status ?? null}
          onChange={val => setFilterReq(pre => ({ ...pre, pageNumber: 1, status: val ?? undefined }))}
        />
      </Stack>
      <Table height={energyHubId ? 500 : 600} data={items} loading={loading} rowHeight={64}>
        <Column width={80} align="center">
          <HeaderCell>Photo</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={0.4}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        {!energyHubId && (
          <Column flexGrow={1.3} fullText>
            <HeaderCell>Hub</HeaderCell>
            <Cell>{(r: EnergyHubReceipt) => <Link to={`/energy-hubs/${r.energyHubId}`}>{r.hubName}</Link>}</Cell>
          </Column>
        )}
        <Column flexGrow={1.2} fullText>
          <HeaderCell>Runner</HeaderCell>
          <Cell>{(r: EnergyHubReceipt) => <Link to={`/users/${r.userId}`}>{r.userName ?? `#${r.userId}`}</Link>}</Cell>
        </Column>
        <Column flexGrow={1.1}>
          <HeaderCell>Submitted (HK)</HeaderCell>
          <Cell fullText>{(r: EnergyHubReceipt) => jsDateToZone(r.submittedAt, energyHubTimeZone)}</Cell>
        </Column>
        <Column flexGrow={1.6} fullText>
          <HeaderCell>Club run</HeaderCell>
          <Cell>
            {(r: EnergyHubReceipt) => (
              <>
                {r.matchSource != EnergyHubReceiptMatchSource.None && (
                  <Tag size="sm" color={r.matchSource == EnergyHubReceiptMatchSource.Runner ? 'blue' : 'violet'} style={{ marginRight: 6 }}>
                    {EnergyHubReceiptMatchSourceList.find(m => m.value == r.matchSource)?.label}
                  </Tag>
                )}
                {r.clubEventName ?? (r.status == EnergyHubReceiptStatus.Approved ? 'Solo' : '-')}
              </>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.7} align="right">
          <HeaderCell>Amount</HeaderCell>
          <Cell>{(r: EnergyHubReceipt) => formatHkd(r.amount)}</Cell>
        </Column>
        <Column flexGrow={0.8} align="center">
          <HeaderCell>Status</HeaderCell>
          <Cell>{(r: EnergyHubReceipt) => <EnergyHubReceiptStatusTag status={r.status} />}</Cell>
        </Column>
        <Column width={100} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: 14 }}>
            {(r: EnergyHubReceipt) => (
              <Button size="sm" appearance={r.status == EnergyHubReceiptStatus.Pending ? 'primary' : 'ghost'} onClick={() => setReviewing(r)}>
                {r.status == EnergyHubReceiptStatus.Pending ? 'Review' : 'View'}
              </Button>
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
      <ReceiptReviewDrawer
        open={!!reviewing}
        receipt={reviewing}
        onClose={() => setReviewing(undefined)}
        onReviewed={() => { setReviewing(undefined); setReloadKey(k => k + 1); onReviewed?.(); }}
      />
    </PageContent>
  );
};

export default EnergyHubReceiptList;
